import React, { InputHTMLAttributes, PropsWithChildren } from "react";
import { Checkbox, Radio, styled, Tooltip } from "@mui/material";
import { borders, dividerStyles } from "./common";
import { WithGrow } from "@common/components/WithGrow";
import Typography from "@common/components/Typography";
import { responsiveFontSize } from "@common/components/Typography/style";
const cssCenterContent = {
  display: "flex",
  verticalAlign: "middle",
  alignItems: "center",
  alignContent: "center",
  justifyContent: "center",
};

const firstCellSx = { ...cssCenterContent, justifyContent: "flexStart" };

export const TableBodyRow = styled("tr")(({ theme }) => ({
  ...responsiveFontSize(theme, "small"),
  "@-moz-document url-prefix()": {
    height: "0%",
  },
}));

export const FirstCellContainer = styled("td")(({ theme }) => ({
  position: "sticky",
  left: 0,
  zIndex: 2,
  backgroundColor: theme.palette.background.default,
  borderRight: borders(theme),
  height: "inherit",
}));
export const FirstCell = styled("div")(() => ({
  height: "100%",
  ...cssCenterContent,
  justifyContent: "flex-start",
}));

export const CellContainer = styled("td")(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(0.5, 0),
  height: "inherit",
  "&>*": {
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    ...cssCenterContent,
  },
  "&:not(:last-child)::after": dividerStyles(theme),
}));
export const HighlightedCellContainer = styled(CellContainer)(({ theme }) => ({
  "&>*": {
    backgroundColor: theme.palette.background.highlighter,
  },
}));
export const DisabledCellContainer = styled(CellContainer)(({ theme }) => ({
  "&:last-child": {
    color: theme.palette.grey["500"],
  },
}));
export const BasicCell = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
}));

export const LeftRoundedCell = styled(BasicCell, {
  shouldForwardProp: (prop) =>
    prop !== "isLastColumn" &&
    prop !== "selectable" &&
    prop !== "pinFirstColumn",
})<{
  isLastColumn?: boolean;
  selectable?: boolean;
  pinFirstColumn?: boolean;
}>(({ theme, isLastColumn, selectable, pinFirstColumn }) => ({
  borderTopLeftRadius: theme.spacing(1.5),
  borderBottomLeftRadius: theme.spacing(1.5),
  ...(isLastColumn && {
    borderTopRightRadius: theme.spacing(1.5),
    borderBottomRightRadius: theme.spacing(1.5),
  }),
  ...(selectable &&
    !pinFirstColumn && {
      padding: theme.spacing(0, 0),
    }),
  marginLeft: theme.spacing(2),
}));
export const RightRoundedCell = styled(BasicCell)(({ theme }) => ({
  borderTopRightRadius: theme.spacing(1.5),
  borderBottomRightRadius: theme.spacing(1.5),
  marginRight: theme.spacing(2),
}));

const OverflowText = (props: React.PropsWithChildren) => {
  const { children } = props;

  return (
    <>
      {typeof children === "string" ? (
        <Tooltip title={children} arrow>
          <Typography
            size="small"
            sx={{
              maxWidth: "300px",
              textAlign: "center",
              wordBreak: "break-all",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {children}
          </Typography>
        </Tooltip>
      ) : (
        <>{children}</>
      )}
    </>
  );
};
type TooltipRowProps = {
  displayTooltip: boolean;
  tooltipText: string;
};
export const Cell = <T, K extends keyof T, O>(
  props: PropsWithChildren<{
    length: number;
    index: number;
    pinFirstColumn: ListViewProps<T, K, O>["pinFirstColumn"];
    onSelect?: () => void;
    highlighted?: boolean;
    selectable: boolean;
    isRowSelectable: boolean;
    checked: boolean;
    disabledCheckbox?: boolean;
    selectionBehavior?: ListViewProps<T, K, O>["selectionBehavior"];
    customSelector?: React.FC<SelectorBehaviorProps>;
    notSelectableReason?: string;
  }>
) => {
  const {
    length,
    index,
    children,
    pinFirstColumn,
    selectable,
    onSelect = () => {},
    isRowSelectable,
    checked,
    disabledCheckbox,
    selectionBehavior,
    customSelector,
    highlighted = false,
    notSelectableReason,
  } = props;

  const isDisabled = !isRowSelectable && Boolean(notSelectableReason);

  const TooltipCell = ({
    displayTooltip,
    tooltipText,
    children,
  }: PropsWithChildren<TooltipRowProps>) => {
    return displayTooltip ? (
      <Tooltip title={tooltipText} arrow>
        <span>{children}</span>
      </Tooltip>
    ) : (
      <>{children}</>
    );
  };

  const withCheckbox = (
    <WithGrow
      direction="row"
      fixed={
        <TooltipCell
          displayTooltip={isDisabled}
          tooltipText={notSelectableReason ?? ""}
        >
          {selectionBehavior === "checkbox" ||
            (selectionBehavior === undefined && !customSelector && (
              <Checkbox
                checked={isRowSelectable && checked}
                disabled={!isRowSelectable || disabledCheckbox}
                onChange={() => onSelect()}
                slotProps={{
                  input: {
                    "data-testid": "body-row-checkbox",
                  } as InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            ))}
          {selectionBehavior === "radio" && (
            <Radio
              checked={checked}
              onChange={() => onSelect()}
              disabled={!isRowSelectable || disabledCheckbox}
            />
          )}
          {customSelector &&
            customSelector({
              checked: isRowSelectable && checked,
              disabled: !isRowSelectable || disabledCheckbox,
              onChange: () => onSelect(),
            })}
        </TooltipCell>
      }
      sx={index === 0 && pinFirstColumn ? firstCellSx : cssCenterContent}
      containerSx={
        index === 0 && pinFirstColumn ? firstCellSx : cssCenterContent
      }
    >
      {children}
    </WithGrow>
  );

  const HighlightableCellContainer = highlighted
    ? HighlightedCellContainer
    : isDisabled
    ? DisabledCellContainer
    : CellContainer;

  if (index === 0 && pinFirstColumn) {
    return (
      <FirstCellContainer>
        <FirstCell>{selectable ? withCheckbox : children}</FirstCell>
      </FirstCellContainer>
    );
  } else if (
    (index === 0 && !pinFirstColumn) ||
    (index === 1 && pinFirstColumn)
  ) {
    return (
      <HighlightableCellContainer sx={{ paddingRight: 0 }}>
        <LeftRoundedCell
          isLastColumn={index === length - 1}
          selectable={selectable}
          pinFirstColumn={pinFirstColumn}
        >
          {selectable && index === 0 ? (
            withCheckbox
          ) : (
            <OverflowText>{children}</OverflowText>
          )}
        </LeftRoundedCell>
      </HighlightableCellContainer>
    );
  } else if (index === length - 1) {
    return (
      <HighlightableCellContainer>
        <RightRoundedCell>
          <OverflowText>{children}</OverflowText>
        </RightRoundedCell>
      </HighlightableCellContainer>
    );
  } else {
    return (
      <HighlightableCellContainer sx={{ paddingRight: 0 }}>
        <BasicCell>
          <OverflowText>{children}</OverflowText>
        </BasicCell>
      </HighlightableCellContainer>
    );
  }
};
